import {
  DEVICE_TYPE,
  ENTRY_POINT_DELIMITER,
  ENTRYPOINT_MISSING,
  EXPECTED_QUERY_ORDER,
  LocalStorageKeys,
  months,
  UrlType,
  VIDEO_SOURCE,
  VIDEO_TYPE,
  VIDEO_TYPE_OLD_QBG,
} from 'src/constants/constants';
import { CLIENT_TYPE, VideoContainerType } from 'src/constants/type';
import { isOTT } from 'src/lib/utilities';
import { LinkType } from 'src/shared/core/video/lowDataMode/enums/enums';
import { getCohortId } from 'src/usePlayerParams';

import { LocalStorageInstance } from './localStroage/LocalStorageService';
import { logger } from './logger/loggerService';

const userAgent = navigator.userAgent;

type EventParams = Record<string, any>;
export const getClientId = () => {
  const fromClientIdKey = localStorage.getItem(LocalStorageKeys.CLIENT_ID);
  if (fromClientIdKey) return fromClientIdKey;

  try {
    const raw = localStorage.getItem('AUTH_CONFIG');
    if (raw) {
      const authConfig = JSON.parse(raw);
      if (authConfig?.organizationId) return authConfig.organizationId;
    }
  } catch {
    // ignore corrupt AUTH_CONFIG entries and fall through to env
  }

  return process.env.REACT_APP_PP_ORG_ID;
};

export const isWEBSDKSupported = () => {
  return !isPPAdmin();
};

export const querySplitter = (query: string) => {
  const cookieArr = query.split('&');
  const queryObj: any = {};
  for (let i = 0; i < 3; i++) {
    const cookiePart = cookieArr[i].split(/=([\s\S]*)/); // splits from first occurrence of "="
    queryObj[cookiePart[0]] = cookiePart[1];
  }
  return queryObj;
};

export function inIframe() {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

export const sendMessageToServiceWorker = (command: string, payload: any) => {
  navigator.serviceWorker?.ready
    .then((registration) => {
      console.log('registration', registration?.active, command, payload);
      registration?.active?.postMessage({ command, payload });
    })
    .catch((error) => {
      logger.error('Error getting service worker registration:', error);
    });
};

export function getDomainName() {
  const _tempArr = window.location.host.split('.');
  const splitLength = _tempArr.length;
  const domain = _tempArr[splitLength - 2] + '.' + _tempArr[splitLength - 1];
  return domain;
}

export const SERVICE_WORKER_MESSAGE_TYPE = {
  UPDATE_IS_SAFARI: 'UPDATE_IS_SAFARI',
  UPDATE_HEADERS: 'UPDATE_HEADERS',
  UPDATE_QUERY_PARAMS: 'UPDATE_QUERY_PARAMS',
};

export const getDateTimeFromISO = (time: string): string => {
  const date = new Date(time);
  const month = months[date.getUTCMonth()];
  const day = ('0' + date.getUTCDate()).slice(-2);
  const hours = ('0' + date.getUTCHours()).slice(-2);
  const minutes = ('0' + date.getUTCMinutes()).slice(-2);
  return `${day}${addNumberSuffix(day)} ${month} ${getTimeInMeridian(hours, minutes)}`;
};

export const getTimeInMeridian = (hours: any, minutes: any) => {
  if (hours > 12) return `${hours % 12}:${minutes} PM`;
  else if (hours == 12) return `${hours}:${minutes} PM`;
  else if (hours == 0) return `$12:${minutes} AM`;
  else return `${hours}:${minutes} AM`;
};

export const addNumberSuffix = (day: any) => {
  const lastDigit = day % 10;
  if (lastDigit === 11 || lastDigit === 12 || lastDigit === 13) {
    return 'th';
  }
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const convertToIST = (duration: string) => {
  const dateInIST = new Date(duration);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const dateInUTC = new Date(dateInIST.getTime() - istOffset);
  return dateInUTC;
};
export const getUserNameFromLocalStorage = () => {
  if (typeof window !== 'undefined') {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      const user = JSON.parse(userJson);
      return `${user.firstName} ${user.lastName}`;
    }
  }
  return '';
};

export const getGADataPWSignedCookie = (cookieAvailable: boolean, scheduleDetails: any) => {
  return {
    is_cookie_available: cookieAvailable,
    schedule_id: scheduleDetails?._id || '',
  };
};

export const getGADataPwVideoPlayable = (
  responseCode: number,
  responseSuccess: boolean,
  scheduleDetails: any,
) => {
  return {
    response_code: responseCode,
    response_success: responseSuccess,
    schedule_id: scheduleDetails?._id || '',
  };
};
export const getGADataPwConvivaTrackingInitialized = (scheduleDetails: any) => {
  return {
    content_type: scheduleDetails?.urlType,
    is_live: (scheduleDetails?.videoDetails && scheduleDetails?.isLive) || false,
    schedule_id: scheduleDetails?._id || '',
  };
};
export const getGADataPwPlayerLoad = (scheduleDetails: any) => {
  return {
    content_type: scheduleDetails?.urlType,
    schedule_id: scheduleDetails?._id || '',
  };
};

export const getEmbedUrl = (url: string) => {
  if (url.includes('youtube.com/watch')) {
    const videoId = url.split('v=')[1]?.split('&')[0];
    return `https://www.youtube.com/embed/${videoId}`;
  }
  return url;
};

export const getLinkType = (url: string): LinkType => {
  if (url.endsWith('.glb')) {
    return LinkType.THREE_D_MODEL;
  } else if (url.includes('youtube.com/watch')) {
    return LinkType.URL;
  }
  return LinkType.UNKNOWN;
};

export const removeElementFromArray = (array: any, element: any) => {
  const index = array.indexOf(element);
  if (index > -1) {
    array.splice(index, 1);
  }
  return array;
};
export function isValidPWDomain() {
  const validDomains = [
    'https://staging.physicswallah.live',
    'https://demo.pw.live',
    'https://www.pw.live',
  ];

  if (typeof window.location.href !== 'undefined') {
    const currentDomain = new URL(window.location.href).origin;
    return validDomains.includes(currentDomain);
  }

  return false;
}

export const qualities = ['720p', '480p', '360p', '240p', 'Auto'];
export const qualitiesMapping = [720, 480, 360, 240, 'Auto'];
export const isSafari =
  /^((?!chrome|crios|fxios|android).)*safari/i.test(userAgent) &&
  !/chrome|crios|fxios/i.test(userAgent) &&
  navigator.vendor === 'Apple Computer, Inc.';

export const isEdge = /Edg/i.test(userAgent) || /Edge\/\d+/i.test(userAgent);

export const isChrome =
  /Chrome/i.test(userAgent) &&
  !/Edg/i.test(userAgent) &&
  !/OPR/i.test(userAgent) &&
  !/Brave/i.test(userAgent) &&
  !/SamsungBrowser/i.test(userAgent);

function getBatchIdFromLocalStorage() {
  try {
    const batchData = JSON.parse(localStorage.getItem('BATCH_DATA') || '{}');
    return batchData?._id || null;
  } catch (error) {
    console.error('Failed to get batchId:', error);
    return null;
  }
}

function isOldQbgQueryParams(queryString: string): boolean {
  if (!queryString) return false;
  const actualOrder = queryString.split('&').map((param) => param.split('=')[0]);
  return EXPECTED_QUERY_ORDER.every((key, index) => actualOrder[index] === key);
}

export const isPPAdmin = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const vType = searchParams.get('vType')?.toLowerCase() || '';
  if (vType === CLIENT_TYPE.ADMIN) {
    return true;
  }
  return false;
};

export const createVideoUrlDetailParams = (
  type: UrlType | string,
  videoContainerType?: VideoContainerType,
  src?: string,
  videoId?: string,
  vType?: string,
  childId?: string,
  parentId?: string,
  secondaryParentId?: string,
  secondaryChildId?: string,
  disableCMAF?: boolean,
  metadata?: string,
  isFromCjr?: boolean,
) => {
  if (
    type &&
    vType &&
    [
      UrlType.PENPENCIL_VDO.toLowerCase(),
      UrlType.AWS_VIDEO.toLowerCase(),
      UrlType.PREMIUM_WEB_RTC.toLowerCase(),
    ].includes(type?.toLowerCase() || '')
  ) {
    return {
      type: vType.toUpperCase(),
      childId: childId,
      parentId: parentId,
      secondaryChildId: secondaryChildId,
      secondaryParentId: secondaryParentId,
      videoId: videoId,
      videoUrl: src,
      disableCMAF: disableCMAF,
      videoContainerType: videoContainerType,
      metadata: metadata,
      isFromCjr: isFromCjr,
      enabled: !!videoContainerType,
    };
  }

  const isOldQbg = isOldQbgQueryParams(window.location.search.slice(1));

  if (isOldQbg && src) {
    return {
      type: VIDEO_TYPE_OLD_QBG,
      videoUrl: src,
      videoContainerType,
      enabled: !!videoContainerType,
    };
  }
  return {};
};

export const watchStatsRegisterSessionPayload = (
  sessionId: string,
  entryPoint: string,
  khazanaProgramId: string | undefined,
  contentId: string | undefined,
  scheduleId: string,
  videoUrlDetails: any,
  fetchWatchStatsData: any,
  typeId: string,
  videoId: string,
  playingSource: string,
  isComplete: boolean | string,
  entityId: string,
  batchId: string,
  vType?: string,
  ottTags?: any[],
  metadata?: any,
  videoLength?: number,
  catId?: string,
) => {
  const watchStats = fetchWatchStatsData?.[0];
  const isSkillsSource = playingSource?.toLowerCase() === 'skills';
  const isKhazana = khazanaProgramId || contentId;
  const isOTTMode = isOTT();

  return {
    Online: true,
    elements: [
      {
        sessionId,
        vdoSrc: isOTTMode ? 'PW_OTT' : isSkillsSource ? playingSource : VIDEO_SOURCE,
        vdoType: isOTTMode ? videoUrlDetails?.urlType : VIDEO_TYPE,
        ePoint: isOTTMode
          ? 'OTT_VIDEOS'
          : entryPoint.split(ENTRY_POINT_DELIMITER).slice(0, -1).join(ENTRY_POINT_DELIMITER) ||
            ENTRYPOINT_MISSING,
        cohortId: entryPoint.split(ENTRY_POINT_DELIMITER).pop() || getCohortId(),
        type: isOTTMode ? 'ott' : isKhazana || isSkillsSource ? 'lecture' : 'class',
        tyId: isOTTMode || isSkillsSource ? typeId : isKhazana ? contentId : scheduleId,
        vId: isSkillsSource || isOTTMode ? videoId : videoUrlDetails?.id,
        device: DEVICE_TYPE,
        isLive: videoUrlDetails?.urlType?.toLowerCase() === UrlType.AWS_VIDEO.toLowerCase(),
        isDownloaded: false,
        isComplete: isSkillsSource ? isComplete === 'true' : (watchStats?.isComplete ?? false),
        prgId: isSkillsSource ? entityId : khazanaProgramId || '',
        batchId: isKhazana ? getBatchIdFromLocalStorage() : batchId,
        tags: ottTags,
        catId,
        metadata,
        videoLength,
      },
    ],
  };
};

export const getIsPeriodicSyncEnabled = (): boolean => {
  try {
    const value = localStorage.getItem('periodicSyncEnable');
    return value ? JSON.parse(value) : false;
  } catch (error) {
    console.error('Error reading periodicSyncEnable from localStorage:', error);
    return false;
  }
};

export const checkIsPWOrg = (): boolean => {
  const user = JSON.parse(localStorage.getItem('user') as string);
  const orgId = user?.organization?._id;
  console.log(
    'orgId',
    'PWWebSDKInstance',
    orgId,
    process.env.REACT_APP_PP_ORG_ID,
    'isTrue',
    orgId === process.env.REACT_APP_PP_ORG_ID,
  );
  return orgId === process.env.REACT_APP_PP_ORG_ID;
};

// Get cohortId from journeyHomeData in localStorage for CuriousJr
export const getCohortIdCjr = (): string | null => {
  try {
    const journeyHomeDataStr = LocalStorageInstance.getItem('journeyHomeData');
    if (!journeyHomeDataStr) {
      return null;
    }
    const journeyHomeData = JSON.parse(journeyHomeDataStr);
    const cohortId = journeyHomeData?.data?.metadata?.currentJourney?.cohortId || null;
    return cohortId;
  } catch (error) {
    return null;
  }
};

// Utility function to decode base64 metadata
export const decodeMetadata = (metadata: string) => {
  try {
    const decoded = atob(metadata);
    return JSON.parse(decoded);
  } catch (error) {
    logger.error('Error decoding metadata:', error);
    return null;
  }
};
const decodeCache = (encodedData: string): any => {
  try {
    // Try to decode as base64 first
    const decoded = atob(encodedData);
    return JSON.parse(decoded);
  } catch (error) {
    console.log('Failed to decode cache data:', error);
  }
};

const encodeCache = (data: any): string => {
  try {
    const jsonString = JSON.stringify(data);
    return btoa(jsonString);
  } catch (error) {
    console.log('Failed to encode cache data:', error);
    return JSON.stringify(data);
  }
};

export const updateProgressCache = (scheduleDetails: any) => {
  const ppjProgresCache = sessionStorage.getItem(LocalStorageKeys.PPJ_PROGRESS_CACHE);

  if (ppjProgresCache) {
    const parsedCache = decodeCache(ppjProgresCache);

    const subjectId = scheduleDetails?.batchSubjectId || '';
    const chapterId = scheduleDetails?.chapterId || '';

    if (subjectId && chapterId) {
      // Initialize subject if it doesn't exist
      if (!parsedCache[subjectId]) {
        parsedCache[subjectId] = {};
      }

      // Initialize chapter if it doesn't exist
      if (!parsedCache[subjectId][chapterId]) {
        parsedCache[subjectId][chapterId] = {
          completedLecture: 0,
          completedDPP: 0,
        };
      }

      // Increment completedDPP count
      parsedCache[subjectId][chapterId].completedLecture += 1;

      sessionStorage.setItem(LocalStorageKeys.PPJ_PROGRESS_CACHE, encodeCache(parsedCache));
    }
  }
};

export const sendEventToParent = (eventName: string, eventParams: EventParams = {}) => {
  const messageData = {
    method: 'TRACK_EVENT',
    args: {
      eventName,
      eventParams,
    },
  };

  window.parent.postMessage(messageData, '*');
};
